import { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { Box, Button, LoadingMask, Text, TextButton } from '@zillow/constellation-10';

import { PaymentGatewayContext } from '../PaymentGateway/context';

import { getFreemiumProductByType } from '../../utils/upsellUtils';

import PropertiesActions from '../../actions/PropertiesActions';
import { default as pgActions } from '../PaymentGateway/actions';

import PropertyConstants from '../../constants/PropertyConstants';
import { DISPLAY_CONTEXT } from '../PaymentGateway/constants';
import { PRODUCT_TYPE_NAME_TO_ID } from '../../constants/FreemiumConstants';

import PayAndPublishCSEvents from '../../model/ClickstreamEvent/PayAndPublishCSEvents';

const COMPONENT_SECTION_CONTEXT = {
  TOP: 'top',
  BOTTOM: 'bottom',
};

const propTypes = {
  componentContext: PropTypes.string,
  componentSectionContext: PropTypes.string,
  // need onPublishClick to be handled in EditListingPage
  // sets display payment gateway and isFromPaymentModalSelection
  // for LegacyPaymentGateway and to handle post activation in long form
  onPublishClick: PropTypes.func,
};

const defaultProps = {
  componentContext: DISPLAY_CONTEXT.MODULAR_LISTING,
  componentSectionContext: COMPONENT_SECTION_CONTEXT.TOP,
  onPublishClick: () => {},
};

const PremiumProductOptionsSimple = ({ componentContext, componentSectionContext, onPublishClick }) => {
  const dispatch = useDispatch();

  const {
    reducerDispatch: pgReducerDispatch,
    pgState: { displayPaymentGateway },
  } = useContext(PaymentGatewayContext);

  const { currentProperty, freemiumProducts, user } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      listingAlias: state.properties.currentProperty.listingAlias,
      freemiumProducts: state.properties.currentProperty.freemium.products,
      user: state.user,
    }),
    shallowEqual,
  );

  const upgradedProduct = useMemo(
    () => getFreemiumProductByType(freemiumProducts, PRODUCT_TYPE_NAME_TO_ID.PREMIUM),
    [freemiumProducts],
  );

  const updateDisplayPaymentGateway = (value) => {
    pgActions.updateAllData({
      reducerDispatch: pgReducerDispatch,
      data: {
        displayPaymentGateway: value,
        displayContext: componentContext,
      },
    });
  };

  const updateSelectedProductType = (selectedProductType) => {
    dispatch(
      PropertiesActions.updateCurrentProperty({
        selectedProductType,
      }),
    );
  };

  const handlePublishClick = (isPremium) => {
    const csEventParams = { currentProperty, user };

    if (componentSectionContext === COMPONENT_SECTION_CONTEXT.TOP) {
      if (isPremium) {
        PayAndPublishCSEvents.StartActivatePremiumTop(csEventParams);
      } else {
        PayAndPublishCSEvents.StartActivateFreemiumTop(csEventParams);
      }
    }

    if (componentSectionContext === COMPONENT_SECTION_CONTEXT.BOTTOM) {
      if (isPremium) {
        PayAndPublishCSEvents.StartActivatePremiumBottom(csEventParams);
      } else {
        PayAndPublishCSEvents.StartActivateFreemiumBottom(csEventParams);
      }
    }

    updateSelectedProductType(
      isPremium ? PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM : PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.FREE,
    );

    if (componentContext === DISPLAY_CONTEXT.MODULAR_LISTING) {
      updateDisplayPaymentGateway(true);
      return;
    }

    onPublishClick(
      isPremium ? PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM : PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.FREE,
    );
  };

  return (
    <Box data-testid="PremiumProductOptionsSimple" css={{ textAlign: 'center' }}>
      <Box css={{ marginBottom: '600' }}>
        <Text textStyle="heading-xs" asChild css={{ letterSpacing: '0.05em' }}>
          <p>{upgradedProduct?.price}</p>
        </Text>
        <Text textStyle="body-sm" color="text.subtle" asChild>
          <p>One-time payment</p>
        </Text>
      </Box>

      <div>
        <Button
          tone="brand"
          emphasis="filled"
          fluid={{ default: true, md: false }}
          css={{ display: 'block', margin: '0 auto', marginBottom: '600' }}
          onClick={() => handlePublishClick(true)}
          data-eventid={
            componentSectionContext === COMPONENT_SECTION_CONTEXT.TOP
              ? PayAndPublishCSEvents.StartActivatePremiumTop.data_event_id
              : PayAndPublishCSEvents.StartActivatePremiumBottom.data_event_id
          }
        >
          Publish with premium
        </Button>

        <LoadingMask iconSize="sm" loading={displayPaymentGateway}>
          <TextButton
            asChild
            onClick={() => handlePublishClick(false)}
            data-testid="PremiumProductOptionsSimple-PublishWithoutPremiumButton"
            data-eventid={
              componentSectionContext === COMPONENT_SECTION_CONTEXT.TOP
                ? PayAndPublishCSEvents.StartActivateFreemiumTop.data_event_id
                : PayAndPublishCSEvents.StartActivateFreemiumBottom.data_event_id
            }
          >
            <div>No thanks, publish without premium</div>
          </TextButton>
        </LoadingMask>
      </div>
    </Box>
  );
};

PremiumProductOptionsSimple.propTypes = propTypes;
PremiumProductOptionsSimple.defaultProps = defaultProps;

export { COMPONENT_SECTION_CONTEXT };

export default PremiumProductOptionsSimple;
